import axios from "axios";
import { toast } from "react-toastify";

import {
  Webhook,
  WebhookCreated,
  WebhookCreatePayload,
  WebhookDelivery,
  WebhookDeliveryStatus,
  WebhookSecret,
  WebhookUpdatePayload,
} from "./Webhook";
import { PaginationDetails } from "../../common/types/Pagination";

const WEBHOOKS_API_URL = `${process.env.REACT_APP_API_BASE_URL}/webhooks`;

export interface WebhookEventTypeOption {
  id: number;
  name: string;
}

/**
 * Catalog from GET /api/common/WebhookEventTypes — `name` is the wire-format string
 * (`ticket.created`, etc.) which is what the UI sends back in `subscribedEvents`.
 */
export const fetchWebhookEventTypes = async (): Promise<WebhookEventTypeOption[]> => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/common/WebhookEventTypes`);
    return response.data as WebhookEventTypeOption[];
  } catch {
    return [];
  }
};

export const fetchWebhooks = async (): Promise<Webhook[]> => {
  try {
    const response = await axios.get(WEBHOOKS_API_URL);
    return response.data as Webhook[];
  } catch {
    return [];
  }
};

export const fetchWebhookById = async (id: string): Promise<Webhook> => {
  const response = await axios.get(`${WEBHOOKS_API_URL}/${id}`);
  return response.data as Webhook;
};

export const createWebhook = async (payload: WebhookCreatePayload): Promise<WebhookCreated> => {
  const response = await axios.post(WEBHOOKS_API_URL, payload);
  toast.success("Webhook-ul a fost creat cu succes!");
  return response.data as WebhookCreated;
};

export const updateWebhook = async (payload: WebhookUpdatePayload): Promise<void> => {
  await axios.put(`${WEBHOOKS_API_URL}/${payload.id}`, payload);
  toast.success("Webhook-ul a fost actualizat cu succes!");
};

export const deleteWebhook = async (id: string): Promise<void> => {
  await axios.delete(`${WEBHOOKS_API_URL}/${id}`);
  toast.success("Webhook-ul a fost sters cu succes!");
};

export const rotateWebhookSecret = async (id: string): Promise<WebhookSecret> => {
  const response = await axios.post(`${WEBHOOKS_API_URL}/${id}/rotate-secret`);
  toast.success("Cheia a fost regenerata cu succes!");
  return response.data as WebhookSecret;
};

export const sendTestEvent = async (id: string): Promise<WebhookDelivery> => {
  const response = await axios.post(`${WEBHOOKS_API_URL}/${id}/test`);
  toast.success("Evenimentul de test a fost trimis. Verifica livrarile pentru rezultat.");
  return response.data as WebhookDelivery;
};

export interface PaginatedDeliveries extends PaginationDetails {
  items: WebhookDelivery[];
}

export const fetchDeliveries = async (
  webhookId: string,
  pageNumber: number,
  pageSize: number,
  status?: WebhookDeliveryStatus
): Promise<PaginatedDeliveries> => {
  const params: Record<string, string | number> = { pageNumber, pageSize };
  if (status) {
    params.status = status;
  }
  const response = await axios.get(`${WEBHOOKS_API_URL}/${webhookId}/deliveries`, { params });
  return response.data as PaginatedDeliveries;
};

export const redeliver = async (deliveryId: string): Promise<WebhookDelivery> => {
  const response = await axios.post(`${WEBHOOKS_API_URL}/deliveries/${deliveryId}/redeliver`);
  toast.success("Reincercare programata.");
  return response.data as WebhookDelivery;
};
